import { CSSProperties, ReactNode } from 'react';
import { useNarrow, useViewportNarrow } from '../../../lib/use-narrow';
import { CatchUpSections } from '../catch-up';
import { HomeHeader } from './HomeHeader';

export function DashboardPane({
  children = null,
  scroll = false,
  bare = false,
  home2 = false,
  map = false,
  tightTop = false,
}: {
  children?: ReactNode;
  scroll?: boolean;
  // The empty home takes over the whole pane — no greeting, no catch-up band — so the
  // "One home for everything" intro sits alone, matching Figma `10941:75527` (the same
  // way the empty Your-Agents screen drops its page title).
  bare?: boolean;
  // Home 2: render ONLY the header top (island + greeting + date) — no catch-up
  // band, no dashboard children. Everything after the header is stripped for the redesign.
  home2?: boolean;
  // The map home: the header renders only the top row (island, greeting, date) — the brief is a node ON
  // the map, so the header's brief row is dropped. Implies `home2`.
  map?: boolean;
  // The Overview carries the agents island in its own header, so the pane does not need the ~72px top that
  // clears the FLOATING island on other homes: the content sits 48 from the top instead (desktop only).
  tightTop?: boolean;
}) {
  // The narrow signal is read SYNCHRONOUSLY for the `tightTop` Overview only, and lazily (useNarrow)
  // otherwise. The Overview body reads `useViewportNarrow` itself, so making its pane synchronous keeps
  // the pane inset and the body in step from the first frame (no 48px→24px flash on a mobile load). The
  // other panes host `HomeHeader` / `MapHome`, which still init from `useNarrow` — so they stay on it too,
  // or the pane would paint its 24px mobile inset while the header first-paints its desktop branch and then
  // shifts. Both hooks run every render (rules of hooks); only which value is used depends on `tightTop`.
  const narrowSync = useViewportNarrow();
  const narrowLazy = useNarrow();
  const narrow = tightTop ? narrowSync : narrowLazy;
  return (
    <section
      className="orion-content-pane"
      // The MAP home is fluid: narrower gutters below 1280. It still keeps the shared 900px floor, so below
      // 900 the docked chat sits over the page and the reader scrolls sideways (globals.css).
      data-fluid={map ? 'true' : undefined}
      style={{
        ...styles.pane,
        // Narrow home: the greeting/date top-aligns with the hamburger (Figma
        // 6393-24243), so the top inset matches the hamburger's ~24px offset
        // instead of the 96px band other mobile pages use.
        paddingBlock: narrow ? '24px 40px' : `${tightTop ? '48px' : '72px'} 40px`,
        // The empty home's conveyor bleeds to the region's edges, so drop the 80px
        // page gutter `.orion-content-pane` adds — the centered intro keeps its
        // midpoint, only the full-width band reaches the edges.
        ...(bare ? { paddingInline: 0 } : null),
        overflowY: scroll ? 'auto' : 'hidden',
      }}
    >
      {/* The map home carries its own header inside `MapHome` (the greeting, date, island and the
          "N new updates" pill sit around the map), so the pane renders none. */}
      {!bare && !map && <HomeHeader home2={home2} />}
      {!bare && !home2 && <CatchUpSections />}
      {/* Home 2 renders its bento body as the pane's children (below the header), so — unlike the
          non-home2 dashboard slot — home2 DOES render children. */}
      {scroll ? <div style={styles.scrollChild}>{children}</div> : <div style={styles.fillChild}>{children}</div>}
    </section>
  );
}

const styles: Record<string, CSSProperties> = {
  pane: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    zIndex: 4,
    overflowX: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  // `min-height: 100%` so the scrolled child is AT LEAST the pane's height. Without it the child is
  // auto-height, so a screen whose own root asks for `min-height: 100%` resolves that percentage
  // against an indefinite parent and gets nothing — and anything the screen pushes to its foot with
  // `margin-block-start: auto` lands right after the content instead. Measured on the Overview home at
  // a viewport tall enough not to overflow: its docked composer sat 542px above the pane's bottom.
  // The pane itself is absolutely positioned with both insets, so its own height IS definite.
  scrollChild: { position: 'relative', width: '100%', minHeight: '100%', flexShrink: 0 },
  fillChild: { flex: 1, minHeight: 0, position: 'relative' },
};
